// TeamRoleTreeNode — renders one team as a collapsible header plus a flat
// member table (PRODOPSAI-509; PRODOPSAI-523 entra-title; PRODOPSAI-539
// row-layout redesign).
//
// Each membership is one row (Name | Team role | AD/Entra title | actions) —
// the previous role-group sublayer is gone; the role is now a column. The
// team header carries member count, an "Archived" badge, and an "Add to team"
// button that opens the Add dialog pre-filled to this team.
//
// Collapse state for the team-level node is owned by the parent via a single
// `collapsedIds: Set<string>` + `onToggle`. Stable id: `team:${team.id}`.

import { ChevronDownIcon, ChevronRightIcon } from '@/components/ui/icons';
import { Badge, Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { ITeamMembershipTreeTeam } from '@skystone/shared';
import { TeamMemberRow } from './TeamMemberRow';
import { MEMBER_ROW_GRID } from './team-role-tree-helpers';

export function teamNodeId(teamId: string): string {
  return `team:${teamId}`;
}

interface TeamRoleTreeNodeProps {
  entry: ITeamMembershipTreeTeam;
  collapsedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onMembershipChanged?: (message: string) => void;
  /** Opens the Add dialog pre-filled to this team (PRODOPSAI-539). */
  onAddToTeam?: (teamId: string) => void;
}

export function TeamRoleTreeNode({
  entry,
  collapsedIds,
  onToggle,
  onMembershipChanged,
  onAddToTeam,
}: Readonly<TeamRoleTreeNodeProps>) {
  const nodeId = teamNodeId(entry.team.id);
  const collapsed = collapsedIds.has(nodeId);
  const Chevron = collapsed ? ChevronRightIcon : ChevronDownIcon;
  // Flatten role groups → one row per membership; the role becomes a column.
  const rows = entry.roleGroups.flatMap((group) =>
    group.persons.map((person) => ({ person, group })),
  );

  return (
    <div
      className="rounded-md border border-border bg-card"
      data-testid="team-node"
      data-team-id={entry.team.id}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => onToggle(nodeId)}
          aria-expanded={collapsed === false}
          className="flex flex-1 items-center gap-2 rounded-md text-left hover:bg-muted/50"
        >
          <Chevron className="h-4 w-4 text-muted-foreground" aria-hidden />
          <span className="font-semibold">{entry.team.name}</span>
          {entry.team.archived === true && (
            <Badge variant="secondary" className="text-[10px]">
              Archived
            </Badge>
          )}
          <span className="ml-2 text-xs text-muted-foreground">
            {`${entry.memberCount} member${entry.memberCount === 1 ? '' : 's'}`}
          </span>
        </button>
        {onAddToTeam !== undefined && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddToTeam(entry.team.id)}
            data-testid="add-to-team"
            aria-label={`Add a person to ${entry.team.name}`}
          >
            + Add to team
          </Button>
        )}
      </div>

      {collapsed === false && (
        <div className="px-3 pb-3">
          {rows.length === 0 ? (
            <p className="px-2 py-2 text-sm text-muted-foreground">No members mapped yet.</p>
          ) : (
            <div>
              <div
                className={cn(
                  MEMBER_ROW_GRID,
                  'border-b border-border px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground',
                )}
                data-testid="member-table-header"
              >
                <span>Name</span>
                <span>Team role</span>
                <span>AD/Entra title</span>
                <span className="sr-only">Actions</span>
              </div>
              <div className="mt-1 space-y-0.5">
                {rows.map(({ person, group }) => (
                  <TeamMemberRow
                    key={person.membershipId}
                    person={person}
                    teamId={entry.team.id}
                    teamName={entry.team.name}
                    group={group}
                    onMembershipChanged={onMembershipChanged}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
